import { PushNotifications } from '@capacitor/push-notifications';
import { API_BASE_URL } from '@/config';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import axios from 'axios';

class PushNotificationService {
  constructor() {
    this.isInitialized = false;
    this.token = null;
    this.pendingRegistrations = [];
    this.initializationPromise = null;
  }

  async initialize() {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initialize();
    return this.initializationPromise;
  }

  async _initialize() {
    if (this.isInitialized) return;

    const platform = Capacitor.getPlatform();
    if (platform === 'web') {
      console.log('Push notifications not supported on web');
      return;
    }

    console.log(`Initializing push notifications on ${platform}...`);

    try {
      // Check if we already have permission
      let permissionStatus = await PushNotifications.checkPermissions();
      
      if (permissionStatus.receive === 'granted') {
        console.log('Push notifications permission granted');
        
        // Clear any existing listeners first
        await PushNotifications.removeAllListeners();
        
        // Add error listener first
        PushNotifications.addListener('registrationError', (error) => {
          console.error('🚨 Push registration error:', error);
          // Log the full error object
          console.error('Full error details:', JSON.stringify(error, null, 2));
        });

        // Create a promise for token registration
        const tokenPromise = new Promise((resolve, reject) => {
          PushNotifications.addListener('registration', (tokenData) => {
            console.log('✅ Push registration success, token:', tokenData.value);
            this.token = tokenData.value;
            resolve(tokenData.value);
          });

          // Add a failure timeout
          setTimeout(() => {
            reject(new Error('Registration timeout - device might not be able to reach APNs'));
          }, 30000);
        });

        try {
          console.log('📱 Attempting push registration...');
          await PushNotifications.register();
          console.log('📱 Waiting for token...');
          
          await tokenPromise;
          console.log('✅ Successfully received token');
          this.processPendingRegistrations();
        } catch (error) {
          console.error('❌ Push registration failed:', error.message);
          throw error;
        }

        this.isInitialized = true;
      } else {
        console.log('❌ Push notification permission denied');
      }
    } catch (error) {
      console.error('❌ Error initializing push notifications:', error);
    }
  }

  async registerDeviceToken(userToken) {
    if (!userToken) {
      console.log('⚠️ No user token provided for device registration');
      return { success: false, error: 'No user token provided' };
    }

    // Make sure initialization is complete
    await this.initialize();

    // If token is available, register immediately
    if (this.token) {
      return this.sendTokenToBackend(userToken, this.token);
    }

    // If no token yet, queue for later
    console.log('📋 Token not available yet, queuing registration...');
    if (!this.pendingRegistrations.includes(userToken)) {
      this.pendingRegistrations.push(userToken);
    }
    return { success: false, queued: true };
  }

  async sendTokenToBackend(userToken, deviceToken) {
    try {
      // Get device information
      const deviceInfo = await Device.getInfo();
      const deviceId = await Device.getId();
      const platform = Capacitor.getPlatform();

      const deviceData = {
        token: deviceToken,
        platform: platform === 'ios' ? 'ios' : 'android',
        device_id: deviceId.identifier,
        app_version: deviceInfo.appVersion || '1.0.0',
        device_name: `${deviceInfo.manufacturer} ${deviceInfo.model}`.trim() || platform
      };

      console.log('📱 Attempting to register device with backend:', {
        ...deviceData,
        token: deviceData.token.substring(0, 10) + '...',  // Only log part of the token
        authHeader: `Bearer ${userToken.substring(0, 10)}...`  // Only log part of the auth token
      });

      const response = await axios.post(`${API_BASE_URL}/api/notifications/device-tokens/register/`, deviceData, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Device token successfully registered with backend:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Error registering device token with backend:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      return { success: false, error: error.message, details: error.response?.data };
    }
  }

  async processPendingRegistrations() {
    if (this.pendingRegistrations.length === 0 || !this.token) return;

    console.log(`📋 Processing ${this.pendingRegistrations.length} pending registrations`);
    
    const registrations = [...this.pendingRegistrations];
    this.pendingRegistrations = [];

    for (const userToken of registrations) {
      await this.sendTokenToBackend(userToken, this.token);
    }
  }

  getFCMToken() {
    return this.token;
  }

  isTokenAvailable() {
    return !!this.token;
  }
}

export default new PushNotificationService(); 